/**
 * Export-format validation helpers — shared across wizard, detail-page edit mode,
 * and the server-side PATCH route.  All three surfaces import from here so that
 * validation rules stay in one place and can never diverge.
 *
 * Field names and allowed values are derived from ExportFormatWizardPage.tsx and
 * the /api/export-formats POST handler — if either changes, update here first.
 */
import { validateX937Config } from '../remittance/x937-config.js';
/** All format types the API accepts. */
export const VALID_FORMAT_TYPES = [
    'csv',
    'fixed_width',
    'xml',
    'json',
    'edi',
    'pdf_summary',
    // FINAL-REVIEW I1 — SP4a Task 4 BAI2 lockbox format. No type-specific
    // settings validation below: the record layout (record types, field order,
    // slash-termination) is owned by generateBai2Lockbox, not by admin-editable
    // settings. It falls through to the permissive "other types" branch. It
    // still needs columns (validateFormatColumns applies generically to every
    // type) for the config UI's column list.
    'bai2_lockbox',
    // SP4b Task 1 — X9.37 image cash letter format. Config-specific validation
    // is delegated to validateX937Config (shared with the generator and the
    // web config UI) rather than duplicated inline below.
    'x937',
];
/**
 * F-016 — canonical transaction-payload row grains for `config.transactionGrain`.
 * The single source for the flattener (`transaction-flatten.ts`), the runtime
 * grain resolver (`transaction-render.ts` grainOf), and the admin UI's
 * Transaction grain select (FormatSettingsForm) — keep them from drifting.
 */
export const TRANSACTION_GRAINS = ['transaction', 'check', 'coupon', 'allocation'];
/** Grain used when a transactions-shape export's config omits (or misspells) transactionGrain. */
export const DEFAULT_TRANSACTION_GRAIN = 'check';
// ─── Allowed values (mirrors the wizard select options) ───────────────────────
const ALLOWED_DELIMITERS = [',', '\t', '|', ';'];
const ALLOWED_LINE_ENDINGS = ['lf', 'crlf'];
// XML element names must start with a letter or underscore, then letters/digits/hyphen/underscore/dot.
const XML_NAME_PATTERN = /^[A-Za-z_][A-Za-z0-9_\-.]*$/;
// Date tokens: only YYYY, YY, MM, M, DD, D plus non-letter separator characters.
const DATE_TOKEN_RE = /^(?:YYYY|YY|MM|M|DD|D|[^A-Za-z]+)+$/;
// ─── Settings validator ───────────────────────────────────────────────────────
/**
 * Validates the `config` object for a given format type.
 *
 * Rules mirror what the wizard UI enforces (required fields, allowed values).
 * Additional rules from the API route (e.g. valid formatType) are checked here
 * as well so callers don't have to duplicate them.
 */
export function validateFormatSettings(type, settings) {
    const errors = [];
    const s = settings ?? {};
    if (!VALID_FORMAT_TYPES.includes(type)) {
        errors.push({
            field: 'formatType',
            message: `Invalid format type. Must be one of: ${VALID_FORMAT_TYPES.join(', ')}`,
        });
        // Can't validate type-specific fields when the type itself is unknown.
        return { errors };
    }
    // transactionGrain — optional on every type (unset/empty = document export),
    // but when present it MUST be a known grain: the runtime's grainOf() falls
    // back to check grain for anything unrecognized, so a typo would silently
    // produce a wrong-grain export instead of an error (F-016 follow-up).
    const grain = s.transactionGrain;
    if (grain !== undefined && grain !== null && grain !== '' && !TRANSACTION_GRAINS.includes(grain)) {
        errors.push({
            field: 'transactionGrain',
            message: `Transaction grain must be one of: ${TRANSACTION_GRAINS.join(', ')}`,
        });
    }
    if (type === 'csv') {
        // Delimiter — required; must be one of the wizard's four options.
        if (!s.delimiter) {
            errors.push({ field: 'delimiter', message: 'Delimiter is required' });
        }
        else if (!ALLOWED_DELIMITERS.includes(s.delimiter)) {
            errors.push({
                field: 'delimiter',
                message: `Delimiter must be one of: comma, tab, pipe, or semicolon`,
            });
        }
        // Line ending — required; must be lf or crlf (lowercase, matching wizard values).
        if (!s.lineEnding) {
            errors.push({ field: 'lineEnding', message: 'Line ending is required' });
        }
        else if (!ALLOWED_LINE_ENDINGS.includes(s.lineEnding)) {
            errors.push({
                field: 'lineEnding',
                message: `Line ending must be "lf" or "crlf"`,
            });
        }
    }
    if (type === 'fixed_width') {
        // recordLength — required; must be a positive integer.
        const rl = s.recordLength;
        if (rl === undefined || rl === null || rl === '') {
            errors.push({ field: 'recordLength', message: 'Record length is required' });
        }
        else if (typeof rl !== 'number' || !Number.isInteger(rl) || rl <= 0) {
            errors.push({ field: 'recordLength', message: 'Record length must be a positive integer' });
        }
        // padChar — required; must be exactly one character.
        const pc = s.padChar;
        if (!pc && pc !== ' ') {
            errors.push({ field: 'padChar', message: 'Pad character is required' });
        }
        else if (typeof pc === 'string' && pc.length !== 1) {
            errors.push({ field: 'padChar', message: 'Pad character must be exactly one character' });
        }
        // Line ending — required for fixed-width as well (default is "lf" in wizard).
        if (!s.lineEnding) {
            errors.push({ field: 'lineEnding', message: 'Line ending is required' });
        }
        else if (!ALLOWED_LINE_ENDINGS.includes(s.lineEnding)) {
            errors.push({
                field: 'lineEnding',
                message: `Line ending must be "lf" or "crlf"`,
            });
        }
    }
    if (type === 'xml') {
        // rootElement — required; must be a valid XML name.
        const root = s.rootElement ?? '';
        if (!root.trim()) {
            errors.push({ field: 'rootElement', message: 'Root element name is required' });
        }
        else if (!XML_NAME_PATTERN.test(root)) {
            errors.push({ field: 'rootElement', message: 'Root element name must be a valid XML identifier' });
        }
        // documentElement — required; must be a valid XML name.
        const doc = s.documentElement ?? '';
        if (!doc.trim()) {
            errors.push({ field: 'documentElement', message: 'Document element name is required' });
        }
        else if (!XML_NAME_PATTERN.test(doc)) {
            errors.push({ field: 'documentElement', message: 'Document element name must be a valid XML identifier' });
        }
    }
    if (type === 'x937') {
        for (const msg of validateX937Config(s))
            errors.push({ field: 'config', message: msg });
        return { errors };
    }
    // json and other types have no required config fields (all have sensible defaults).
    return { errors };
}
function validateFormatSpec(col, i, errors) {
    const fmt = col.format;
    if (!fmt)
        return;
    if (col.dataType !== undefined && col.dataType !== fmt.kind) {
        errors.push({ field: `columns[${i}].dataType`, message: 'Column dataType must match its format kind' });
    }
    if (fmt.kind === 'date') {
        if (!fmt.token || !DATE_TOKEN_RE.test(fmt.token)) {
            errors.push({ field: `columns[${i}].format`, message: 'Date token must be non-empty and use only YYYY/YY/MM/M/DD/D tokens' });
        }
    }
    else if (fmt.kind === 'number') {
        if (fmt.decimals != null && (!Number.isInteger(fmt.decimals) || fmt.decimals < 0)) {
            errors.push({ field: `columns[${i}].format`, message: 'Decimals must be a non-negative integer' });
        }
    }
    else if (fmt.kind === 'text') {
        if (fmt.maxLength != null && (!Number.isInteger(fmt.maxLength) || fmt.maxLength <= 0)) {
            errors.push({ field: `columns[${i}].format`, message: 'maxLength must be a positive integer' });
        }
        if (fmt.pad && (!Number.isInteger(fmt.pad.to) || fmt.pad.to <= 0 || fmt.pad.char.length !== 1)) {
            errors.push({ field: `columns[${i}].format`, message: 'Pad requires a positive width and a single-character fill' });
        }
    }
    if (col.note != null && col.note.length > 500) {
        errors.push({ field: `columns[${i}].note`, message: 'Schema note must be 500 characters or fewer' });
    }
}
/**
 * Validates the columns array for an export format.
 *
 * Common rules (all types):
 *  - At least one column required
 *  - Every column must have a non-empty name
 *  - Column names must be unique
 *
 * Fixed-width rules:
 *  - `length` must be a positive integer when present
 *
 * SP5 — computed column expressions (seam choice):
 *  A column's `expression` is only legal at transaction grain
 *  (`config.transactionGrain === 'transaction'`). This function — not a new
 *  sibling — owns that check: `config` and `validateExpression` are both
 *  appended as new TRAILING OPTIONAL params, so every existing call site
 *  (which passes only `type`/`cols`) keeps compiling unchanged. `shared`
 *  cannot import `@capture/rules-engine` (rules-engine depends on shared —
 *  importing it back would be circular), so the actual expression-syntax
 *  check (`validateTransactionExpression`) is injected by the caller
 *  (the API PATCH route, the web columns editor). The grain check itself
 *  needs no injection and always runs when a column has an expression.
 */
export function validateFormatColumns(type, cols, config, validateExpression) {
    const errors = [];
    if (!Array.isArray(cols) || cols.length === 0) {
        errors.push({ field: 'columns', message: 'At least one column is required' });
        return { errors };
    }
    const seen = new Set();
    const isTransactionGrain = config?.transactionGrain === 'transaction';
    cols.forEach((col, i) => {
        const nameTrimmed = col.name?.trim() ?? '';
        if (!nameTrimmed) {
            errors.push({
                field: `columns[${i}].name`,
                message: 'Column name is required',
            });
        }
        else if (seen.has(nameTrimmed)) {
            errors.push({
                field: `columns[${i}].name`,
                message: `Duplicate column name: ${col.name}`,
            });
        }
        else {
            seen.add(nameTrimmed);
        }
        // Fixed-width: validate per-column length when supplied.
        if (type === 'fixed_width' && col.length !== undefined) {
            if (typeof col.length !== 'number' || !Number.isInteger(col.length) || col.length <= 0) {
                errors.push({
                    field: `columns[${i}].length`,
                    message: 'Column length must be a positive integer',
                });
            }
        }
        // SP5 — computed column expressions: transaction-grain-only, expression
        // syntax checked via the injected validator when provided.
        pushExpressionErrors(col, i, isTransactionGrain, validateExpression, errors);
        validateFormatSpec(col, i, errors);
    });
    return { errors };
}
// ─── Computed-column expression validator (SP5) ───────────────────────────────
function pushExpressionErrors(col, i, isTransactionGrain, validateExpression, errors) {
    const expr = col.expression?.trim();
    if (!expr)
        return;
    if (!isTransactionGrain) {
        errors.push({
            field: `columns[${i}].expression`,
            message: 'Computed column expressions are only allowed when the format\'s transaction grain is "transaction"',
        });
    }
    else if (validateExpression) {
        for (const msg of validateExpression(expr)) {
            errors.push({ field: `columns[${i}].expression`, message: msg });
        }
    }
}
/**
 * SP5 — expression-only column validation for the STRUCTURE save paths.
 *
 * The real UI save shape is `structure` (flatColumnsToStructure), and the
 * server re-derives `columns` via deriveColumnsFromStructure. Those derived
 * columns cannot flow through full `validateFormatColumns`: tree scopes
 * legally repeat leaf names across collections (validateFormatStructure's
 * per-scope uniqueness), and full column validation would reject that flat
 * projection. This targeted validator applies ONLY the computed-column rules
 * (grain gate + injected expression syntax) so the save-time wall guards the
 * structure path exactly as validateFormatColumns guards the raw-columns
 * path. Both delegate to the same per-column logic — one source of truth.
 */
export function validateComputedColumns(cols, config, validateExpression) {
    const errors = [];
    const isTransactionGrain = config?.transactionGrain === 'transaction';
    (cols ?? []).forEach((col, i) => pushExpressionErrors(col, i, isTransactionGrain, validateExpression, errors));
    return { errors };
}
// ─── Structure (tree) validator ───────────────────────────────────────────────
/**
 * Validates the format-owned structure tree (groups/collections/field slots).
 *
 * Rules:
 *  - At least one node required.
 *  - Every node (group, collection, field) must have a non-empty trimmed name.
 *  - Sibling names must be unique within each container scope; the same name may
 *    repeat in a different scope.
 *
 * Type/dataType checks are intentionally out of scope here (that is FormatSpec
 * territory). Error `field` keys use index paths (e.g. `structure[0].children[1]`)
 * so a duplicate or empty name still yields a unique, stable key.
 */
export function validateFormatStructure(nodes) {
    const errors = [];
    if (!Array.isArray(nodes) || nodes.length === 0) {
        errors.push({ field: 'structure', message: 'At least one field is required' });
        return { errors };
    }
    const walk = (list, prefix) => {
        const seen = new Set();
        list.forEach((node, i) => {
            const path = `${prefix}[${i}]`;
            const trimmed = node.name?.trim() ?? '';
            if (!trimmed) {
                errors.push({ field: `${path}.name`, message: 'Name is required' });
            }
            else if (seen.has(trimmed)) {
                errors.push({ field: `${path}.name`, message: `Duplicate name: ${node.name}` });
            }
            else {
                seen.add(trimmed);
            }
            if (node.children && node.children.length > 0) {
                walk(node.children, `${path}.children`);
            }
        });
    };
    walk(nodes, 'structure');
    return { errors };
}
//# sourceMappingURL=export-format-validation.js.map